/**
 * AvatarStack - Displays online collaborators in header
 *
 * Shows up to 3 avatars with "+N" overflow indicator (per CONTEXT.md).
 * Click avatar to jump to and follow that collaborator.
 *
 * Design inspired by Google Docs collaborator display.
 */

import type { PresenceUser } from '@deep-agents/shared-types';

interface AvatarStackProps {
  // List of online users (excluding self)
  users: PresenceUser[];
  // Max avatars to show before "+N"
  maxVisible?: number;
  // Click handler when avatar clicked
  onUserClick?: (user: PresenceUser) => void;
}

export function AvatarStack({
  users,
  maxVisible = 3,
  onUserClick,
}: AvatarStackProps) {
  const visibleUsers = users.slice(0, maxVisible);
  const overflowCount = users.length - maxVisible;

  if (users.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center -space-x-2">
      {visibleUsers.map((user) => (
        <button
          key={user.id}
          onClick={() => onUserClick?.(user)}
          className="relative h-8 w-8 rounded-full border-2 border-white transition-transform hover:z-10 hover:scale-110"
          style={{ backgroundColor: user.color }}
          title={`${user.name} (click to follow)`}
        >
          {/* Avatar - initials or image */}
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.name}
              className="h-full w-full rounded-full object-cover"
            />
          ) : (
            <span className="flex h-full w-full items-center justify-center text-xs font-medium text-white">
              {user.name.charAt(0).toUpperCase()}
            </span>
          )}

          {/* Online indicator dot */}
          <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white bg-green-500" />
        </button>
      ))}

      {/* Overflow indicator */}
      {overflowCount > 0 && (
        <div
          className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-gray-200 text-xs font-medium text-gray-600"
          title={`${overflowCount} more collaborator${overflowCount > 1 ? 's' : ''}`}
        >
          +{overflowCount}
        </div>
      )}
    </div>
  );
}
